import React from "react";
import { PARTICIPANT_STATUS, ParticipantStatus } from "@cede/types";
import { HoldToEarnCampaign, REWARD_RULE, HOLDING_EPOCH_STATUS } from "@cede/types";
import { formatBalance } from "@cede/utils";
import { getHoldRewardRule } from "./holdCampaignFormatters";
import { HOLD_CAMPAIGN_STATUS, USER_PARTICIPATION_STATUS } from "@/constants";
import { HoldCampaignWithStatus, HoldCampaignStatusType } from "@/types/holdCampaigns";
import { isWithinInterval } from "date-fns";
import { CircleMinus, CircleDashed, CircleCheckBig, PauseCircle } from "lucide-react";

export type HoldCampaignUserStatus = (typeof USER_PARTICIPATION_STATUS)[keyof typeof USER_PARTICIPATION_STATUS];

export interface HoldCampaignUserStatusInfo {
  status: HoldCampaignUserStatus;
  statusLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeColor: string;
  borderColor: string;
  backgroundColor: string;
  explanation: string;
}

interface GetHoldCampaignUserStatusProps {
  isParticipating: boolean;
  isOptedOut: boolean;
  userRecord?: {
    heldAmount: number;
    rewardAmount?: number;
  } | null;
}

export const getHoldCampaignWithStatus = (campaign: HoldToEarnCampaign): HoldCampaignWithStatus => {
  const now = new Date();
  const startDate = new Date(campaign.startAt || new Date() || new Date());
  const endDate = new Date(campaign.endAt || new Date());

  let status: HoldCampaignStatusType;
  if (now < startDate) {
    status = HOLD_CAMPAIGN_STATUS.FUTURE;
  } else if (now > endDate) {
    status = HOLD_CAMPAIGN_STATUS.PAST;
  } else {
    status = HOLD_CAMPAIGN_STATUS.LIVE;
  }

  // Find current epoch based on campaign epochs
  const currentEpoch = getCurrentHoldCampaignEpoch(campaign);

  // Check if the campaign should be marked as paused based on current epoch
  if (status === HOLD_CAMPAIGN_STATUS.LIVE && currentEpoch && currentEpoch.status === HOLDING_EPOCH_STATUS.PENDING) {
    // If we're in the campaign timeframe but the current epoch is paused, mark campaign as paused
    status = HOLD_CAMPAIGN_STATUS.PAUSED;
  }

  // Extract exchangeIds from current/first epoch if not available at campaign level
  let exchangeIds = campaign.exchangeIds || [];
  if ((!exchangeIds || exchangeIds.length === 0) && campaign.epochs && campaign.epochs.length > 0) {
    // Use current epoch if available, otherwise fallback to first epoch
    const sourceEpoch = currentEpoch || campaign.epochs[0];

    if (sourceEpoch) {
      exchangeIds = sourceEpoch.exchangeIds || [];
    }
  }

  return {
    ...campaign,
    exchangeIds, // Override with extracted exchangeIds
    status,
    isEligible: false, // TODO: Implement eligibility logic
    currentHeldAmount: 0, // Will be populated from leaderboard data
    participantCount: 0, // Will be populated from leaderboard data
    apr: campaign.apr || 0, // APR computed by backend
    currentEpoch,
  };
};

/**
 * Determine the current epoch for a hold campaign
 */
export const getCurrentHoldCampaignEpoch = (campaign: HoldToEarnCampaign) => {
  const now = new Date();

  // If campaign has epochs defined, find the current one
  if (campaign.epochs && campaign.epochs.length > 0) {
    const currentEpoch = campaign.epochs.find((epoch) => {
      const epochStart = new Date(epoch.startDate || new Date());
      const epochEnd = new Date(epoch.endDate || new Date());
      return isWithinInterval(now, { start: epochStart, end: epochEnd });
    });

    if (currentEpoch) {
      return {
        ...currentEpoch, // Include all backend epoch properties
        status: currentEpoch.isPaused ? HOLDING_EPOCH_STATUS.PENDING : HOLDING_EPOCH_STATUS.ACTIVE,
        isActive: true,
      };
    }
  }

  // Return null if no current epoch found
  return null;
};

/**
 * Get current epoch ID for leaderboard queries
 */
export const getCurrentEpochNumber = (campaign: HoldToEarnCampaign | null): number | null => {
  if (!campaign) return null;

  const currentEpoch = getCurrentHoldCampaignEpoch(campaign);
  return currentEpoch?.epochNumber ?? null;
};

export const getHoldCampaignAPR = (campaign: HoldCampaignWithStatus): string => {
  // For past campaigns with completion data, show the max APR achieved
  if (campaign.status === HOLD_CAMPAIGN_STATUS.PAST && campaign.completionData?.maxApr) {
    const maxApr = parseFloat(campaign.completionData.maxApr);
    return `${formatBalance(maxApr * 100, { roundForFiat: true, preventRoundingUp: true })}%`;
  }
  // Show "-" for past campaigns without completion data
  if (campaign.status === HOLD_CAMPAIGN_STATUS.PAST) {
    return "-";
  }
  return `${formatBalance(campaign.apr * 100, { roundForFiat: true, preventRoundingUp: true })}%`;
};

export const getHoldCampaignCurrentAmount = (
  campaign: HoldCampaignWithStatus,
  leaderboardStats?: { totalHeldAmount: number; totalHeldAmountUsd?: number },
): string => {
  // For past campaigns with completion data, show the max total held amount
  if (campaign.status === HOLD_CAMPAIGN_STATUS.PAST && campaign.completionData?.maxTotalHeldAmount) {
    // Prefer USD value if available and > 0 (based on token price snapshot at campaign end)
    const usdAmount = parseFloat(campaign.completionData.maxTotalHeldAmountUsd || "0");
    if (usdAmount > 0) {
      return `$${formatBalance(usdAmount, { roundForFiat: true, preventRoundingUp: true, showSmallDigits: true })}`;
    }

    // Fallback to token amount if USD not available or is 0
    const maxAmount = parseFloat(campaign.completionData.maxTotalHeldAmount);
    return `${formatBalance(maxAmount, { roundForFiat: true, preventRoundingUp: true, showSmallDigits: true })} ${
      campaign.tokenSymbol
    }`;
  }

  // Show "-" for past campaigns without completion data or upcoming campaigns
  if (campaign.status === HOLD_CAMPAIGN_STATUS.PAST || campaign.isUpcoming) {
    return "-";
  }

  // Prefer USD value if available
  const usdAmount = leaderboardStats?.totalHeldAmountUsd;
  if (usdAmount !== undefined && usdAmount > 0) {
    return `$${formatBalance(usdAmount, { roundForFiat: true, preventRoundingUp: true, showSmallDigits: true })}`;
  }

  // Fallback to token amount if USD not available
  const amount = leaderboardStats?.totalHeldAmount ?? campaign.currentHeldAmount;
  return `${formatBalance(amount, { roundForFiat: true, preventRoundingUp: true, showSmallDigits: true })} ${
    campaign.tokenSymbol
  }`;
};

export const getHoldCampaignRewardModel = (campaign: HoldCampaignWithStatus): string => {
  const rewardRule = getHoldRewardRule(campaign);

  if (rewardRule === REWARD_RULE.PROPORTIONAL) {
    return "Daily pro-rata pool";
  } else {
    return "Fixed spot-based rewards";
  }
};

export const getHoldCampaignUserParticipantStatus = ({
  isParticipating,
  isOptedOut,
  userRecord,
}: GetHoldCampaignUserStatusProps): ParticipantStatus => {
  if (isOptedOut) {
    return PARTICIPANT_STATUS.OPTED_OUT;
  } else if (!isParticipating) {
    return PARTICIPANT_STATUS.NOT_PARTICIPATING;
  } else if (userRecord && userRecord.heldAmount > 0 && (userRecord.rewardAmount || 0) > 0) {
    return PARTICIPANT_STATUS.HOLDING;
  } else {
    return PARTICIPANT_STATUS.PARTICIPATING;
  }
};

// New function to handle backend participant status enum
export const getHoldCampaignUserStatusFromEnum = (status: ParticipantStatus): HoldCampaignUserStatusInfo => {
  switch (status) {
    case PARTICIPANT_STATUS.HOLDING:
      return {
        status: USER_PARTICIPATION_STATUS.HOLDING,
        statusLabel: "Holding",
        icon: CircleCheckBig,
        badgeColor: "bg-green-500/20 text-green-400 border-green-500/20",
        borderColor: "border-green-500/20",
        backgroundColor: "bg-green-500/5",
        explanation: "You're actively holding tokens and earning rewards based on your position.",
      };
    case PARTICIPANT_STATUS.PARTICIPATING:
      return {
        status: USER_PARTICIPATION_STATUS.PARTICIPATING,
        statusLabel: "Participating",
        icon: CircleDashed,
        badgeColor: "bg-blue-500/20 text-blue-400 border-blue-500/20",
        borderColor: "border-blue-500/20",
        backgroundColor: "bg-blue-500/5",
        explanation: "You're registered for the campaign. Start holding tokens to appear on the leaderboard.",
      };
    case PARTICIPANT_STATUS.OPTED_OUT:
      return {
        status: USER_PARTICIPATION_STATUS.OPTED_OUT,
        statusLabel: "Opted Out",
        icon: PauseCircle,
        badgeColor: "bg-orange-500/20 text-orange-400 border-orange-500/20",
        borderColor: "border-orange-500/20",
        backgroundColor: "bg-orange-500/5",
        explanation: "You've opted out of this campaign and won't receive rewards.",
      };
    case PARTICIPANT_STATUS.NOT_PARTICIPATING:
    default:
      return {
        status: USER_PARTICIPATION_STATUS.NOT_PARTICIPATING,
        statusLabel: "Not Participating",
        icon: CircleMinus,
        badgeColor: "bg-cede-text-grey/20 text-cede-text-secondary border-cede-text-grey/20",
        borderColor: "border-cede-text-grey/20",
        backgroundColor: "bg-cede-text-grey/5",
        explanation: "Join the campaign through the trading widget to start earning rewards.",
      };
  }
};
